// @ts-strict-ignore
import React, { useContext } from 'react';
import { Icon, IconName, Tooltip } from '@usealloy/component-library';
import {
  buildDynamicRHSOptions,
  buildStaticRHSOptions,
  getSelectedRHSOption,
  serializeRHSMultiSelectValue,
  getRightAttributeValue,
  getMultiSelectRHSValue,
  getCustomPlaceholder,
  filterSelectableRHSOptions,
} from './helpers';
import { doesActionSupportTooltip } from 'flow/editor/components/Graph/nodes/BaseNode/helpers';
import {
  selectActions,
  selectIndexedReasonCodeGroups,
  selectNodeLineageForAnchorNode,
  selectRuleBuilderActiveSubEditor,
  selectRuleBuilderPanelBoxContainerWidth,
  selectWorkflow,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useAppSelector, useAppDispatch } from '~/store/reduxHooks';
import { StyledPre } from 'flow/editor/styles';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import { getIsPreview } from '../../../helpers';
import { routes } from '~/helpers/routes';
import { configureReasonCodeGroupOption } from './constants';
import {
  buildAllAttributeOptions,
  computeRuleItemLengthLimit,
  getMultiSelectProps,
  getSelectedOption,
  getShouldHideTooltip,
} from '../helpers';
import { RuleBuilderContext } from '../../../context';
import { ThresholdContext } from '../context';
import { AGGREGATION_EDIT_OPTION_LABEL, MLA_ATTRIBUTE_EDIT_OPTION_LABEL } from '../constants';
import RuleMultiSelect from '../../RuleTemplate/RuleMultiSelect';
import { RuleBuilderSubEditorSide, RuleBuilderSubEditorType } from 'flow/store/engine/types';
import { setRuleBuilderActiveSubEditor } from 'flow/store/redux/slice';
import { StyledEditableControlContainer } from '../../styles';
import {
  getAggregationName,
} from '~/dashboard/views/workflows/service/services';

interface RHSMultiSelectProps {
  isMultiSelect: boolean;
  shouldUseDefaultString: boolean;
  supportsBestEffortParsing?: boolean;
}

const RHSMultiSelect = ({
  isMultiSelect,
  shouldUseDefaultString,
  supportsBestEffortParsing = false,
}: RHSMultiSelectProps,
) => {
  const {
    sourceAction, editMode, ActionHoverTooltip,
  } = useContext(RuleBuilderContext);
  const { isDynamic, lhsDataType, populatedThreshold } = useContext(ThresholdContext);

  const { reasonCodeGroups, services } = useAppSelector(selectWorkflowProps);
  const indexedReasonCodeGroups = useAppSelector(selectIndexedReasonCodeGroups);
  const actions = useAppSelector(selectActions);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const workflow = useAppSelector(selectWorkflow);
  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);

  const dispatch = useAppDispatch();

  const shouldUseMultiSelect = !isDynamic && isMultiSelect;

  // All options for the RHS, contains deprecated values (like reason code groups that are archived).
  const options = isDynamic ? buildDynamicRHSOptions({
    actions,
    lengthLimit,
    lhsDataType,
    nodeLineage,
    populatedThreshold,
    services,
    sourceAction,
    workflow,
  }) : buildStaticRHSOptions({
    lengthLimit,
    lhsDataType,
    populatedThreshold,
    reasonCodeGroups,
  });

  // All selectable options for the RHS, contains only valid options.
  const selectableOptions = filterSelectableRHSOptions({
    options,
    lhsDataType,
    indexedReasonCodeGroups,
  });

  const selectedOptionValue = serializeRHSMultiSelectValue({
    isDynamic,
    lhsDataType,
    populatedThreshold,
  });

  // The selected option for the RHS; this can contain deprecated values.
  const selectedOption = selectedOptionValue
    ? getSelectedRHSOption({ options, value: selectedOptionValue })
    : [];

  const isDynamicServiceAction = isDynamic
    && isActionOfType(populatedThreshold.right.action, ActionType.SERVICE);

  const rhsAggregationName = getAggregationName(populatedThreshold.right);

  const attributeOptions = isDynamicServiceAction ? buildAllAttributeOptions({
    service: services[populatedThreshold.right.service?.internal_name],
    workflow,
    RHSOptions: {
      leftDataType: lhsDataType,
      leftAction: populatedThreshold.left.action,
      rightAction: populatedThreshold.right.action,
    },
    currentAttribute: {
      attributeId: populatedThreshold.right.attributeId,
      attribute: populatedThreshold.right.attribute,
    },
    aggregationDisplayName: rhsAggregationName,
  }) : [];

  const selectedAttributeOption = isDynamicServiceAction ? getSelectedOption({
    incomingValue: String(populatedThreshold.right.attributeId),
    options: attributeOptions,
  }) : null;

  const hasNoReasonCodes = (
    lhsDataType === 'reasoncodes'
    && options.length === 1 && options[0].value === 'no reason codes'
  );

  const customPlaceholder = getCustomPlaceholder({
    hasNoReasonCodes,
    isMultiSelect: shouldUseMultiSelect,
    lhsDataType,
    shouldUseDefaultString,
  });

  const ctaOption = lhsDataType === 'reasoncodes'
    ? [{
      icon: <Icon color="blue" icon={IconName.ExternalService} size="small" />,
      ...configureReasonCodeGroupOption,
    }]
    : null;

  const handleChange = (selected: DropdownOption | DropdownOption[]) => {
    if (!Array.isArray(selected)) {
      if (selected.value === 'configure_reason_code_groups') {
        window.open(routes.v3.dashboard.settings.reason_codes, '_blank').focus();
        return;
      }
    }

    if (Array.isArray(selected)) {
      const value = getMultiSelectRHSValue({ selected, supportsBestEffortParsing });
      dispatch(updateThreshold.rightValue(populatedThreshold.publicId, value));
      return;
    }

    if (lhsDataType === 'reasoncodes') {
      const reasonCodeGroupId = Number(selected.value);
      dispatch(updateThreshold.reasonCodeGroupId(
        populatedThreshold.publicId,
        reasonCodeGroupId,
      ));

      const reasonCodeGroup = reasonCodeGroups.find(rcg => rcg.id === reasonCodeGroupId);
      dispatch(updateThreshold.reasonCodeGroup(
        populatedThreshold.publicId,
        reasonCodeGroup,
      ));
      return;
    }

    if (isActionOfType(populatedThreshold.left.action, ActionType.TAG)) {
      dispatch(updateThreshold.leftTagResult(populatedThreshold.publicId, selected.value === 'true'));
      return;
    }

    if (isDynamic) {
      dispatch(updateThreshold.rightAction(populatedThreshold.publicId, selected.value));
      return;
    }

    const value = getRightAttributeValue({
      lhsDataType,
      shouldUseDefaultString,
      value: selected.value,
    });

    dispatch(updateThreshold.rightValue(populatedThreshold.publicId, value));
  };

  const handleAttributeChange = (option: DropdownOption) => {
    if (option.label === MLA_ATTRIBUTE_EDIT_OPTION_LABEL) {
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.MLA_EDITOR,
          side: RuleBuilderSubEditorSide.RHS,
        }),
      );
      return;
    }

    if (option.label === AGGREGATION_EDIT_OPTION_LABEL) {
      dispatch(updateThreshold.rightDynamicAttribute(
        populatedThreshold.publicId,
        Number(option.value),
      ));
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.AGGREGATIONS_EDITOR,
          side: RuleBuilderSubEditorSide.RHS,
        }),
      );
      return;
    }

    dispatch(updateThreshold.rightDynamicAttribute(
      populatedThreshold.publicId,
      Number(option.value),
    ));
  };

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold.right.action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const tooltipContent = isDynamic
    && ActionHoverTooltip
    && doesActionSupportTooltip(populatedThreshold.right.action) ? (
      <ActionHoverTooltip
        key={populatedThreshold.right.action.publicId}
        action={populatedThreshold.right.action}
      />
    ) : null;

  return (
    <>
      {shouldUseMultiSelect && <StyledPre>is</StyledPre>}
      <Tooltip
        align="start"
        content={tooltipContent}
        side="top"
        variant="secondary"
        hide={(
          !tooltipContent
          || getShouldHideTooltip(selectedOption)
        )}
      >
        <StyledEditableControlContainer>
          <RuleMultiSelect
            {...multiSelectProps}
            openMenuOnFocus
            ctaOptions={ctaOption}
            options={selectableOptions}
            onChange={handleChange}
            placeholder={customPlaceholder}
            selectedOptions={selectedOption}
            isMulti={!!shouldUseMultiSelect}
            isNaked
          />
        </StyledEditableControlContainer>
      </Tooltip>
      {isDynamicServiceAction && selectedAttributeOption && (
        <RuleMultiSelect
          {...multiSelectProps}
          options={attributeOptions}
          onChange={handleAttributeChange}
          selectedOptions={selectedAttributeOption}
          isMulti={false}
          isNaked
        />
      )}
    </>
  );
};

export default RHSMultiSelect;
